import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { backendClient } from '@/lib/api-client';
import { useMatterMemory, type EcaProgressParams } from '@/hooks/useMatterMemory';

const EXTRACTION_TYPE_EARLY_CASE_ASSESSMENT = 'early_case_assessment';

export type EarlyCaseGenerateBody = {
  file_reference_guids: string[];
  folder_guids: string[];
};

export type EcaGenerateOptions = {
  regenerate?: boolean;
  /** Super-admin one-off gauging overrides, forwarded as query params. */
  model?: string;
  verbosity?: string;
};

export function buildEcaGenerateBodyFromSelection(selectionIds: string[]): EarlyCaseGenerateBody {
  const file_reference_guids: string[] = [];
  const folder_guids: string[] = [];
  for (const id of selectionIds) {
    if (id.startsWith('file:')) {
      file_reference_guids.push(id.slice('file:'.length));
    } else if (id.startsWith('folder:')) {
      folder_guids.push(id.slice('folder:'.length));
    }
  }
  return { file_reference_guids, folder_guids };
}

// Poll progress until the lock state satisfies `predicate`, or `maxIterations`
// 5s ticks elapse. `is_processing` mirrors the Celery extraction lock, so this
// is how we observe the worker acquiring (or releasing) it.
async function pollEarlyCaseProcessing(
  matterGuid: string,
  body: EarlyCaseGenerateBody | undefined,
  signal: AbortSignal,
  predicate: (isProcessing: boolean) => boolean,
  maxIterations: number,
): Promise<void> {
  // No body => regenerate from persisted picks; omit the pick params so
  // progress resolves against the saved selection rather than empty scope.
  const params = new URLSearchParams();
  body?.file_reference_guids.forEach((g) => params.append('eca_file_reference_guid', g));
  body?.folder_guids.forEach((g) => params.append('eca_folder_guid', g));
  const qs = params.toString();
  const url = qs
    ? `/matter-memory/${matterGuid}/progress?${qs}`
    : `/matter-memory/${matterGuid}/progress`;

  for (let i = 0; i < maxIterations; i += 1) {
    if (signal.aborted) {
      return;
    }

    try {
      const { data } = await backendClient.get(url, {
        headers: { extraction_type: EXTRACTION_TYPE_EARLY_CASE_ASSESSMENT },
        signal,
      });
      if (predicate(Boolean(data.is_processing))) {
        return;
      }
    } catch (error: unknown) {
      // Ignore transient polling errors; keep waiting unless canceled.
      if (signal.aborted) {
        return;
      }
    }

    await new Promise<void>((resolve) => {
      const timeout = setTimeout(resolve, 5000);
      signal.addEventListener(
        'abort',
        () => {
          clearTimeout(timeout);
          resolve();
        },
        { once: true },
      );
    });
  }
}

/** Poll progress until the current ECA extraction is unlocked. */
async function waitUntilEarlyCaseNotProcessing(
  matterGuid: string,
  body: EarlyCaseGenerateBody | undefined,
  signal: AbortSignal,
): Promise<void> {
  await pollEarlyCaseProcessing(matterGuid, body, signal, (isProcessing) => !isProcessing, 720);
}

// Cap on how long we hold the optimistic regenerate lock waiting for the worker
// to pick the job up (~2 minutes at 5s/tick). User-initiated jobs are normally
// claimed within seconds; on timeout we release and fall back to the backend
// freshness signal, erring toward staying locked rather than re-enabling
// mid-flight.
const PROCESSING_START_MAX_ITERATIONS = 24;

/** Poll progress until the worker has acquired the ECA extraction lock. */
async function waitUntilEarlyCaseProcessing(
  matterGuid: string,
  body: EarlyCaseGenerateBody | undefined,
  signal: AbortSignal,
): Promise<void> {
  await pollEarlyCaseProcessing(
    matterGuid,
    body,
    signal,
    (isProcessing) => isProcessing,
    PROCESSING_START_MAX_ITERATIONS,
  );
}

export function useEarlyCaseAssessmentContent(
  matterGuid: string | undefined,
  ecaProgressParams: EcaProgressParams = null,
) {
  const {
    matterMemory,
    progress,
    contributingDocumentsCount,
    ecaExplicitFileReferenceGuids,
    ecaExplicitFolderGuids,
    ecaDroppedPersistedPickCount,
    ecaOverDocumentLimit,
    ecaDocumentLimit,
    ecaEffectiveDocumentCount,
    freshnessStatus,
    generationReason,
    loadingSummary,
    summaryError,
    isGenerating,
    fetchMatterMemory,
    fetchProgress,
    markGenerationPending,
  } = useMatterMemory(matterGuid, EXTRACTION_TYPE_EARLY_CASE_ASSESSMENT, ecaProgressParams);

  const [queuedRegenerate, setQueuedRegenerate] = useState(false);
  const isMountedRef = useRef(true);
  const pollingAbortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      pollingAbortControllerRef.current?.abort();
    };
  }, []);

  const triggerEarlyCaseGenerate = useCallback(
    async (body: EarlyCaseGenerateBody | undefined, options?: EcaGenerateOptions) => {
      if (!matterGuid) return;

      const regenerate = options?.regenerate ?? false;
      const params = new URLSearchParams();
      if (regenerate) params.set('regenerate', 'true');
      // Super-admin gauging overrides ride on the same auth-scoped generate
      // route (enforced server-side via require_super_admin), so the regenerate
      // keeps the existing same-task-id idempotency instead of a parallel admin
      // path that races the self-heal GET.
      if (options?.model) params.set('model', options.model);
      if (options?.verbosity) params.set('verbosity', options.verbosity);
      const generateQuery = params.toString();
      const generateUrl = generateQuery
        ? `/matter-memory/${matterGuid}/generate?${generateQuery}`
        : `/matter-memory/${matterGuid}/generate`;

      pollingAbortControllerRef.current?.abort();
      const pollingAbortController = new AbortController();
      pollingAbortControllerRef.current = pollingAbortController;

      // Optimistically lock regeneration on the first click so the button
      // disables immediately, before the POST + freshness refetch round-trip.
      if (regenerate && isMountedRef.current) {
        setQueuedRegenerate(true);
      }

      try {
        let { data } = await backendClient.post(
          generateUrl,
          body ?? null,
          { headers: { extraction_type: EXTRACTION_TYPE_EARLY_CASE_ASSESSMENT } },
        );
        if (data.status === 'already_processing') {
          if (isMountedRef.current) {
            setQueuedRegenerate(true);
            toast.success('Early case assessment is running; your latest selection will start when it finishes.');
          }
          await waitUntilEarlyCaseNotProcessing(
            matterGuid,
            body,
            pollingAbortController.signal,
          );
          if (!isMountedRef.current || pollingAbortController.signal.aborted) {
            return;
          }
          // Keep `queuedRegenerate` held across the re-POST so the control stays
          // locked through the hand-off rather than briefly re-enabling.
          ({ data } = await backendClient.post(
            generateUrl,
            body ?? null,
            { headers: { extraction_type: EXTRACTION_TYPE_EARLY_CASE_ASSESSMENT } },
          ));
          if (data.status === 'already_processing') {
            if (isMountedRef.current) {
              toast.error('Generation is still busy. Try again in a few seconds.');
            }
            return;
          }
        }
        if (data.status === 'generation_started') {
          if (isMountedRef.current) {
            toast.success(
              regenerate
                ? 'Early case assessment regeneration started'
                : 'Early case assessment generation started',
            );
          }
        }
        // For a regenerate we must observe the worker holding the lock BEFORE
        // issuing the self-heal GET. `GET /matter-memory/{guid}` re-enqueues
        // generation when it sees no lock + stale/needs-generation content, and
        // that duplicate runs with the production-default model/verbosity. If it
        // wins the semaphore it silently drops our override run (the worker
        // returns status "locked", requeued: false). Waiting first makes the GET
        // observe `is_processing` and skip the enqueue, so the override is the
        // only queued run. It also lets `freshness_status` settle to `running`
        // (progress polling then keeps the control locked until `finally`
        // releases `queuedRegenerate`). Regenerating up-to-date content can
        // otherwise land in the gap between enqueue and lock acquisition and
        // report up_to_date / not generating.
        //
        // Non-regenerate generates carry no overrides, so their self-heal
        // duplicate is a harmless same-model no-op; the GET fires immediately.
        if (regenerate && data.status === 'generation_started') {
          await waitUntilEarlyCaseProcessing(
            matterGuid,
            body,
            pollingAbortController.signal,
          );
          if (!isMountedRef.current || pollingAbortController.signal.aborted) {
            return;
          }
        }

        await fetchMatterMemory();
        await fetchProgress();
      } catch (error: unknown) {
        if (!isMountedRef.current || pollingAbortController.signal.aborted) {
          return;
        }
        const err = error as { response?: { data?: { detail?: string } } };
        toast.error(err?.response?.data?.detail || 'Failed to trigger early case assessment generation');
      } finally {
        // Only the current invocation owns the lock lifecycle. A superseded run
        // (its controller already replaced by a newer trigger) must not clear
        // the optimistic lock the newer trigger just set.
        if (pollingAbortControllerRef.current === pollingAbortController) {
          pollingAbortControllerRef.current = null;
          if (isMountedRef.current) {
            setQueuedRegenerate(false);
          }
        }
      }
    },
    [matterGuid, fetchMatterMemory, fetchProgress],
  );

  return {
    markdown: matterMemory,
    progress,
    contributingDocumentsCount,
    ecaExplicitFileReferenceGuids,
    ecaExplicitFolderGuids,
    ecaDroppedPersistedPickCount,
    ecaOverDocumentLimit,
    ecaDocumentLimit,
    ecaEffectiveDocumentCount,
    freshnessStatus,
    generationReason,
    loadingSummary,
    summaryError,
    isGenerating: isGenerating || queuedRegenerate,
    fetchEarlyCaseAssessment: fetchMatterMemory,
    fetchProgress,
    triggerEarlyCaseGenerate,
    markEarlyCaseGenerationPending: markGenerationPending,
  };
}
